import {
  ButtonColor,
  ButtonSize,
  Modal,
  ModalType,
  Table,
  TableRow,
} from '@code/innovance-react-ui-kit';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  ClientInfoDocumentPageValidation,
  ClientInfoTaxPageValidation,
  ContactInfoChange,
  Document,
  DocumentStorage,
  ProductOperationsContactInfoChange,
} from 'src/front/api/apiInstance';

import { ReferenceDataType, ScreenProcessType, UploadType } from 'src/front/api/generated';

import usePRM from 'src/front/common/hooks/usePRM';
import {
  createDocumentFormData,
  createDocumentUpdateData,
  uploadDocuments,
} from 'src/front/common/utils/documentUploadUtils';
import displayPDF from 'src/front/common/utils/domUtils';

import { dictionaryLineFormat } from 'src/front/common/utils/formUtils';

import preview from 'src/front/common/utils/previewUtils';

import AsyncButton from 'src/front/components/AsyncButton';

import UploadDocumentModal, {
  UploadDocumentFormData,
} from 'src/front/components/document/UploadDocumentModal';

import {
  Container,
  FormRow,
  InfoLabel,
  InfoRow,
  LineSeparator,
  Separator,
  Subtitle,
  TableContainer,
  TableWrapper,
  Title,
  WarningText,
} from 'src/front/components/FormStyles';
import Markdown from 'src/front/components/Markdown';
import SuccessButtonText from 'src/front/components/SuccessButtonText';
import { useClearDispatch, useCustomerSearchState } from 'src/front/contexts/ContactInfoContext';
import styled from 'styled-components';

const StyledButton = styled(AsyncButton)`
  width: 300px;
  padding-left: 60px;

  text-align: left;
`;

const documentTableHeaders = [
  'general_documentType_label',
  'general_note_label',
  'general_uploadedDate_text',
];

interface Props {
  onRepeat: () => void;
  setIsCompleted?: (value: boolean) => void;
  isEdit?: boolean;
  contactCustomerName?: string;
}

const ContactInfoDocumentUpload: FC<Props> = ({
  onRepeat,
  setIsCompleted,
  isEdit,
  contactCustomerName,
}) => {
  const { t } = useTranslation();
  const { clientName, customerId } = useCustomerSearchState();
  const documentTypes = usePRM(ReferenceDataType.CUSTOMERDOCUMENTS);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [warning, setWarning] = useState<string | undefined>('');
  const [showDocumentModal, setShowDocumentModal] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const [selectedDocument, setSelectedDocument] = useState<TableRow>();
  const [documentFormValues, setDocumentFormValues] = useState<UploadDocumentFormData>();
  const [documents, setDocuments] = useState<UploadDocumentFormData[]>([]);

  const [showDocumentUploadForm, setShowDocumentUploadForm] = useState<boolean>(false);

  const [isApplicationCompleted, setIsApplicationCompleted] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [executedOperationCompletedMessage, setExecutedOperationCompletedMessage] = useState<
    string
  >();

  const clearSearchInputs = useClearDispatch();

  const loadDocuments = useCallback(async (): Promise<void> => {
    try {
      const { data } = await ClientInfoDocumentPageValidation.getClientInfoDocumentPageData();
      if (data.result === 'OK' && data.pageData && data.pageData.documentUploadDataList) {
        const uploadedDocumentList =
          data.pageData.documentUploadDataList.map((document) => ({
            documentType: documentTypes.find(
              (type) => type.value === document.documentType.code
            ) || {
              value: document.documentType.code,
              label: document.documentType.code,
            },
            explanation: document.explanation || '',
            isOldDocument: document.isOldDocument,
            documentId: document.documentId,
            createDate: document.createDate,
            documentName: document.documentName,
          })) || [];
        setDocuments(uploadedDocumentList);
      }
    } catch (error) {
      // TODO (abdulbasid): show error on modal or handle with error boundary
    }
  }, [documentTypes]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    return () => {
      ClientInfoTaxPageValidation.getClientInfoTaxPageData({
        documentUploadDataList: documents.map((document) => ({
          ...document,
          documentType: dictionaryLineFormat(document.documentType),
        })),
      });
      ClientInfoDocumentPageValidation.validateClientInfoDocumentPageData({
        documentUploadDataList: documents.map((document) => ({
          ...document,
          documentType: dictionaryLineFormat(document.documentType),
        })),
      });
    };
  }, [documents]);

  const handleSubmit = async (): Promise<void> => {
    const hasNoDocument = !documents.length;
    if (hasNoDocument) {
      setWarning('general_noUploadedDocumentTable_text');
    } else {
      try {
        setIsLoading(true);
        if (isEdit) {
          const {
            data: response,
          } = await ProductOperationsContactInfoChange.updateContactInfoChangeDataByReferenceNumber(
            {
              documentUploadDataList: documents.map((document) => ({
                ...document,
                documentType: dictionaryLineFormat(document.documentType),
              })),
            }
          );
          if (response.result === 'OK') {
            setExecutedOperationCompletedMessage(response.contactInfoChangeResultMessage);
            setIsCompleted && setIsCompleted(true);
          }
        } else {
          const { data: response } = await ContactInfoChange.updateContactInfo({
            documentUploadDataList: documents.map((document) => ({
              ...document,
              documentType: dictionaryLineFormat(document.documentType),
            })),
          });
          if (response.result === 'OK') {
            setSuccessMessage(response.updateResultMessage);
            setIsApplicationCompleted(true);
            setIsCompleted && setIsCompleted(true);
          }
        }
      } catch (error) {
        setIsLoading(false);
      }
    }
  };

  const handleDownloadApplicationClick = async (): Promise<void> => {
    try {
      const { data } = await ContactInfoChange.generateContactInfoChangeDocumentId({
        screenProcessType: isEdit ? ScreenProcessType.EDIT : ScreenProcessType.NEW,
      });

      if (data.result === 'OK') {
        const { data: response } = await DocumentStorage.getDocument({
          documentId: data.documentId,
        });

        if (response.result === 'OK') {
          displayPDF(response.documentData);
          setShowDocumentUploadForm(true);
        }
      }
    } catch (err) {
      // todo(abdulbasid): it's handled with error boundary
    }
  };

  const closeDocumentModal = (): void => setShowDocumentModal(false);

  const handleDocumentSubmit = async (formData: UploadDocumentFormData): Promise<void> => {
    let newDocuments;
    const { documentId, documentType } = formData;

    if (!documentId && documentType) {
      const data = await uploadDocuments(formData, UploadType.CONTACTINFO, customerId);

      if (data.result === 'OK') {
        newDocuments = [createDocumentFormData(data, formData, documentTypes), ...documents];
        warning && setWarning(undefined);
      }
    } else if (documentType) {
      const { data } = await Document.updateDocument(
        createDocumentUpdateData(formData, UploadType.CONTACTINFO, customerId)
      );
      if (data.result === 'OK') {
        newDocuments = documents.map((doc) =>
          doc.documentId === documentId
            ? createDocumentFormData(data, formData, documentTypes)
            : doc
        );
      }
    }
    setDocuments(newDocuments || []);
    setShowDocumentModal(false);
  };
  const handleExecutedOperationCompletedModal = (): void => {
    setExecutedOperationCompletedMessage(undefined);
    onRepeat && onRepeat();
  };

  const handleApplicationSuccessClick = (): void => {
    setIsApplicationCompleted(false);
    clearSearchInputs();
    onRepeat();
    setIsCompleted && setIsCompleted(false);
  };

  const handleDeleteDocument = async (): Promise<void> => {
    const { data } = await Document.deleteDocument({
      documentId: selectedDocument?.id,
      uploadType: UploadType.CONTACTINFO,
    });
    if (data.result === 'OK') {
      setDocuments(documents.filter((doc) => doc.documentId !== selectedDocument?.id));
      setSelectedDocument(undefined);
      setIsDeleteModalOpen(false);
    }
  };

  const addDocument = useCallback((): void => {
    setDocumentFormValues(undefined);
    setShowDocumentModal(true);
  }, []);

  const editDocument = useCallback(
    (item?: TableRow): void => {
      setDocumentFormValues(documents.find((doc) => doc.documentId === item?.id));
      setShowDocumentModal(true);
    },
    [documents]
  );
  const previewDocument = useCallback(
    (item?: TableRow): void => {
      preview(documents, item);
    },
    [documents]
  );
  const deleteDocument = useCallback((item?: TableRow): void => {
    setIsDeleteModalOpen(true);
  }, []);

  const actions = useMemo(
    () => ({
      add: addDocument,
      edit: editDocument,
      delete: deleteDocument,
      preview: previewDocument,
    }),
    [addDocument, deleteDocument, editDocument, previewDocument]
  );

  const documentTableData: TableRow[] = documents.map(
    (document: UploadDocumentFormData): TableRow => ({
      id: document.documentId,
      columns: [document.documentType?.label || '', document.explanation, document.createDate],
      deletable: !document.isOldDocument,
      editable: !document.isOldDocument,
      previewable: !document.isOldDocument,
    })
  );

  return (
    <Container>
      {isApplicationCompleted ? (
        <Modal
          description={<Markdown text={successMessage} />}
          onCancel={handleApplicationSuccessClick}
          submitText={t('general_components_close_button')}
          title={t('general_wellDone_text')}
          type={ModalType.Success}
        />
      ) : null}

      <Title>
        <Trans i18nKey="changeFundClientInfo_title" />
      </Title>

      <InfoRow>
        <InfoLabel>
          <Trans i18nKey="changeFundClientInfo_clientName_text" />:
        </InfoLabel>
        {contactCustomerName || clientName?.clientName}
      </InfoRow>
      <FormRow>
        <LineSeparator />
      </FormRow>
      <Separator />

      <Subtitle>
        <Trans i18nKey="changeFundClientInfo_ClientInfoChangeDocument_text" />
      </Subtitle>

      <StyledButton
        color={ButtonColor.Secondary}
        onClick={handleDownloadApplicationClick}
        size={ButtonSize.Medium}
        text={
          <SuccessButtonText
            isSuccess={showDocumentUploadForm}
            label={t('general_download_button')}
          />
        }
        type="button"
      />

      {showDocumentModal ? (
        <UploadDocumentModal
          documentTypes={documentTypes}
          formValues={documentFormValues}
          onClose={closeDocumentModal}
          submit={handleDocumentSubmit}
        />
      ) : null}
      {isDeleteModalOpen ? (
        <Modal
          cancelText={t('general_cancel_button')}
          description={t('general_deleteRecordConfirm_text')}
          onCancel={() => setIsDeleteModalOpen((state) => !state)}
          onSubmit={handleDeleteDocument}
          submitText={t('general_components_confirm_button')}
          title="Confirmation"
          type={ModalType.Confirmation}
        />
      ) : null}
      {executedOperationCompletedMessage ? (
        <Modal
          description={executedOperationCompletedMessage}
          onCancel={handleExecutedOperationCompletedModal}
          submitText={t('general_components_close_button')}
          title={t('general_wellDone_text')}
          type={ModalType.Success}
        />
      ) : null}
      <Separator />
      {showDocumentUploadForm ? (
        <>
          <Subtitle>
            <Trans i18nKey="changeFundClientInfo_uploadDocument_subtitle" />
          </Subtitle>
          <TableWrapper>
            <TableContainer>
              <Table
                actionConfig={actions}
                addActionText={t('general_add_button')}
                emptyTableText={t('cardEntry_documentEntry_noUploadedDocumentFound_text')}
                headers={documentTableHeaders.map((head) => t(head))}
                items={documentTableData}
                onSelect={setSelectedDocument}
                pageNumber={1}
                selectedItem={selectedDocument}
                widths={['40', '30', '30']}
              />
            </TableContainer>
          </TableWrapper>
          <WarningText>
            <Trans i18nKey={warning} />
          </WarningText>

          <Separator />
          <FormRow>
            <AsyncButton
              color={ButtonColor.Primary}
              disabled={isLoading}
              onClick={handleSubmit}
              size={ButtonSize.Medium}
              text={t('general_continue_button')}
              type="button"
            />
          </FormRow>
        </>
      ) : null}
    </Container>
  );
};

export default ContactInfoDocumentUpload;
